import React, { useState, useEffect, useRef } from 'react';
import { SearchIcon } from '@heroicons/react/solid';
import { fetchOrders, fetchOrderById } from '../../orders-controller/order-controller';
import { Spinner } from '../../../../components/Spinner';

const OrderSearch = ({ onSelectOrder }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const searchRef = useRef(null);

  // Format date to be more readable
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Format price with null check
  const formatPrice = (price) => {
    if (price === null || price === undefined) {
      return '0.00 TND';
    }
    return `${parseFloat(price).toFixed(2)} TND`;
  };

  // Load orders based on search term
  const loadOrders = async (resetPage = false) => {
    try {
      setIsLoading(true);
      
      const currentPage = resetPage ? 1 : page;
      
      const response = await fetchOrders(
        currentPage,
        10,
        'delivered', // No status filter
        searchTerm,
        '', // No date filters
        ''
      );
      
      const newOrders = response.list || [];
      
      if (resetPage) {
        setOrders(newOrders);
        setPage(1);
      } else {
        setOrders(prev => [...prev, ...newOrders]);
        setPage(currentPage + 1);
      }
      
      // Check if there are more orders to load
      setHasMore(newOrders.length > 0 && currentPage < response.maxPage);
      
      // Show results if we have a search term or if we have orders
      setShowResults(searchTerm.length > 0 || newOrders.length > 0);
    } catch (error) {
      console.error('Error loading orders:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle search input changes
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle search form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    loadOrders(true);
  };

  // Handle order selection with fetchOrderById
  const handleOrderSelect = async (order) => {
    try {
      setIsLoadingDetails(true);
      
      // Get detailed order information by ID
      const orderDetails = await fetchOrderById(order.id);
      console.log("Order details:", orderDetails); // Debug
      
      // Accessing the correct structure based on the API response
      const entet = orderDetails.entet || {};
      const lines = orderDetails.line || [];
      
      // Transform the order into the format expected by the exchange component
      const transformedOrder = {
        id: entet.id,
        number: entet.reference,
        date: entet.dateBonCommande,
        total: entet.totalTTC || 0,
        customer: {
          name: `${entet.clientFirstName || ''} ${entet.clientLastName || ''}`.trim(),
          email: entet.clientEmail || '',
          phone: entet.clientNumber || ''
        },
        shippingAddress: {
          street: entet.clientAdresse || '',
          city: entet.ville || '',
          zipCode: '',
          country: 'Tunisie'
        },
        items: lines.map(item => {
          // Extract product and variant information
          const product = item.product || {};
          const variant = item.productVariant || {};
          
          // Build a combined product name that includes the variant
          const productName = product.name || 'Produit';
          const variantName = variant.name || '';
          const combinedName = variantName 
            ? `${productName} - ${variantName}` 
            : productName;

          // Process variant color and size if available
          let color = '';
          let size = '';
          
          if (variant.name && variant.name.includes(' - ')) {
            const parts = variant.name.split(' - ');
            color = parts[0] || '';
            size = parts[1] || '';
          }
          
          return {
            id: item.id,
            productId: item.productId,
            variantId: item.productVariantId,
            name: combinedName, // Use the combined name
            sku: item.product?.reference || variant?.reference || 'N/A',
            price: item.prixUnitaire || 0,
            quantity: item.quantite || 0,
            color: color,
            size: size,
            image: (variant.imageUrl && variant.imageUrl[0]) || 
                  (product.imageUrl && product.imageUrl[0]) || 
                  'https://source.unsplash.com/random/300x300/?product',
            total: item.totalTTC || 0,
            tva: item.tauxTva || 19,
            remise: item.tauxRemise || 0
          };
        })
      };
      
      onSelectOrder(transformedOrder);
      setShowResults(false);
    } catch (error) {
      console.error('Error fetching order details:', error);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  // Handle click outside to close results
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Status display helper
  const getOrderStatusBadge = (status) => {
    if (!status) return null;
    
    const statusMap = {
      'en attente': 'bg-yellow-100 text-yellow-800',
      'pending': 'bg-yellow-100 text-yellow-800',
      'en cours': 'bg-blue-100 text-blue-800',
      'processing': 'bg-blue-100 text-blue-800',
      'terminée': 'bg-green-100 text-green-800',
      'completed': 'bg-green-100 text-green-800',
      'annulée': 'bg-red-100 text-red-800',
      'cancelled': 'bg-red-100 text-red-800',
      'livrée': 'bg-purple-100 text-purple-800',
      'delivered': 'bg-purple-100 text-purple-800'
    };
    
    // Convert to lowercase for case-insensitive matching
    const statusClass = statusMap[status.toLowerCase()] || 'bg-gray-100 text-gray-800';
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusClass}`}>
        {status}
      </span>
    );
  };

  return (
    <div className="relative" ref={searchRef}>
      <form onSubmit={handleSubmit} className="flex w-full">
        <div className="relative flex-grow">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <SearchIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
          </div>
          <input
            type="search"
            name="search"
            id="search"
            className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md h-10"
            placeholder="Rechercher par référence, client, email..."
            value={searchTerm}
            onChange={handleSearchChange}
            onFocus={() => setShowResults(true)}
          />
          {isLoading && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              <Spinner size="sm" />
            </div>
          )}
        </div>
        <button
          type="submit"
          className="ml-3 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          Rechercher
        </button>
      </form>

      {showResults && (
        <div className="absolute mt-1 w-full bg-white shadow-lg rounded-md z-10 max-h-96 overflow-y-auto">
          {orders.length === 0 ? (
            <div className="py-8 text-center text-gray-500">
              {isLoading ? (
                <div className="flex justify-center">
                  <Spinner size="md" />
                </div>
              ) : (
                <p>Aucune commande trouvée. Essayez un autre terme de recherche.</p>
              )}
            </div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {orders.map((order) => (
                <li
                  key={order.id}
                  className="px-4 py-4 hover:bg-gray-50 cursor-pointer transition duration-150 ease-in-out"
                  onClick={() => handleOrderSelect(order)}
                >
                  <div className="flex justify-between">
                    <p className="text-sm font-medium text-indigo-600">#{order.reference || ''}</p>
                    {getOrderStatusBadge(order.etat)}
                  </div>
                  <div className="mt-2">
                    <div className="flex justify-between">
                      <p className="text-sm text-gray-900">
                        {order.clientFirstName || ''} {order.clientLastName || ''}
                      </p>
                      <p className="text-sm font-medium text-gray-900">{formatPrice(order.totalTTC)}</p>
                    </div>
                    <div className="flex justify-between mt-1">
                      <p className="text-xs text-gray-500">{formatDate(order.dateBonCommande)}</p>
                      <p className="text-xs text-gray-500">
                        {order.ligne?.length || 0} article{(order.ligne?.length || 0) !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                  {isLoadingDetails && (
                    <div className="mt-2 flex justify-center">
                      <Spinner size="sm" />
                    </div>
                  )}
                </li>
              ))}
              
              {hasMore && (
                <li className="px-4 py-3 text-center">
                  <button
                    type="button"
                    onClick={() => loadOrders(false)}
                    className="text-sm text-indigo-600 hover:text-indigo-900"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <Spinner size="sm" />
                    ) : (
                      'Charger plus de commandes'
                    )}
                  </button>
                </li>
              )}
            </ul>
          )}
        </div>
      )}
      
      {isLoadingDetails && !showResults && (
        <div className="mt-4 p-4 border border-gray-200 rounded-md bg-gray-50">
          <div className="flex items-center justify-center">
            <Spinner size="md" />
            <span className="ml-2 text-sm text-gray-600">Chargement des détails de la commande...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderSearch;